import * as React from 'react';

import { cn } from '../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { Icon } from './icon';

const headBackground = new URL('../../assets/images/head.png', import.meta.url)
  .href;

export type UserCardGender = 'male' | 'female' | 'other';

export type UserCardFieldVal =
  | { readonly type: 'text'; readonly text: string }
  | { readonly type: 'link'; readonly text: string; readonly href: string };

export interface UserCardField {
  readonly id: string;
  readonly fieldKey: string;
  readonly fieldVal: UserCardFieldVal;
}

export interface UserCardTag {
  readonly id: string;
  readonly text: string;
  readonly icon?: React.ReactNode;
}

export interface UserCardProps extends React.ComponentProps<'div'> {
  name: string;
  /** 用户名；展示时自动加 `@` 前缀（已有 `@` 则不再重复）。 */
  username?: string;
  /** Portrait URL; omitted when `avatar` slot is provided. */
  avatarSrc?: string;
  avatarColor?: string;
  /** Replaces the default avatar (image / monogram). */
  avatar?: React.ReactNode;
  /**
   * 性别指示（名字旁）。`male`/`female`（或 `'1'`/`'2'`）→ 22px 圆 + 图标；
   * 其它值 → 青绿胶囊 + 图标 + 文案（`genderLabel` 优先，否则用原始字符串，英文兜底 Other）。
   */
  gender?: string;
  /** `gender` 为 other 时的展示文案；未传则尝试用 `gender` 原串，再兜底 `Other`。 */
  genderLabel?: string;
  bio?: string;
  /** Expand control when bio overflows 3 lines. English fallback. */
  bioMoreLabel?: string;
  bioLessLabel?: string;
  /** 自定义展示行（profile list）：左 fieldKey / 右 fieldVal。 */
  fields?: readonly UserCardField[];
  actions?: React.ReactNode;
}

function displayUsername(username: string): string {
  return username.startsWith('@') ? username : `@${username}`;
}

function resolveGenderIcon(gender: string): UserCardGender {
  const normalized = gender.trim().toLowerCase();
  if (normalized === '1' || normalized === 'male') {
    return 'male';
  }
  if (normalized === '2' || normalized === 'female') {
    return 'female';
  }
  return 'other';
}

function genderIconId(kind: UserCardGender): string {
  switch (kind) {
    case 'male':
      return 'i-gender-male';
    case 'female':
      return 'i-gender-female';
    default:
      return 'i-gender-other';
  }
}

function genderDisplayLabel(
  gender: string,
  kind: UserCardGender,
  genderLabel?: string,
): string {
  if (genderLabel?.trim()) {
    return genderLabel.trim();
  }
  const normalized = gender.trim().toLowerCase();
  if (
    kind === 'other' &&
    normalized !== '1' &&
    normalized !== '2' &&
    normalized !== 'male' &&
    normalized !== 'female' &&
    normalized !== '__empty__'
  ) {
    return gender.trim();
  }
  return 'Other';
}

function splitEmail(value: string): { local: string; domain: string } | null {
  const at = value.indexOf('@');
  if (at <= 0 || at >= value.length - 1) {
    return null;
  }
  return {
    local: value.slice(0, at),
    domain: value.slice(at),
  };
}

function TruncatedFieldText({ text }: { text: string }) {
  const email = splitEmail(text);
  if (email) {
    return (
      <span className="inline-flex max-w-full min-w-0">
        <span className="min-w-0 truncate">{email.local}</span>
        <span className="shrink-0">{email.domain}</span>
      </span>
    );
  }
  return <span className="block truncate">{text}</span>;
}

function FieldValue({ fieldVal }: { fieldVal: UserCardFieldVal }) {
  const className = 'max-w-full min-w-0 text-sm leading-5';

  if (fieldVal.type === 'link') {
    return (
      <a
        href={fieldVal.href}
        className={cn(
          className,
          'inline-block text-primary transition-opacity hover:opacity-80',
        )}
      >
        <TruncatedFieldText text={fieldVal.text} />
      </a>
    );
  }

  return (
    <span className={cn(className, 'block text-foreground')}>
      <TruncatedFieldText text={fieldVal.text} />
    </span>
  );
}

function ProfileFieldRow({ field }: { field: UserCardField }) {
  return (
    <div
      data-slot="user-card-field"
      className="flex w-full items-center gap-2.5 py-2.5"
    >
      <span className="max-w-[124px] min-w-0 shrink-0 truncate text-sm leading-5 text-muted-foreground">
        {field.fieldKey}
      </span>
      <div className="ml-auto min-w-0 max-w-[198px] text-right">
        <FieldValue fieldVal={field.fieldVal} />
      </div>
    </div>
  );
}

function ExpandableBio({
  text,
  moreLabel,
  lessLabel,
}: {
  text: string;
  moreLabel: string;
  lessLabel: string;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const ref = React.useRef<HTMLParagraphElement>(null);
  const [overflowing, setOverflowing] = React.useState(false);
  React.useEffect(() => {
    if (expanded) {
      return;
    }
    const el = ref.current;
    if (!el) {
      return;
    }
    setOverflowing(el.scrollHeight - el.clientHeight > 1);
  }, [text, expanded]);
  return (
    <div
      data-slot="user-card-bio"
      className="flex w-full flex-col items-center gap-1"
    >
      <p
        ref={ref}
        className={cn(
          'text-sm leading-5 text-foreground',
          !expanded && 'line-clamp-3',
        )}
      >
        {text}
      </p>
      {overflowing ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="cursor-pointer text-sm font-medium text-primary transition-opacity hover:opacity-80"
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      ) : null}
    </div>
  );
}

/** male/female → 22px 圆 + 图标；other → 胶囊 + 图标 + 文案；`''` → 空圆点。 */
function Gender({
  gender,
  genderLabel,
}: {
  gender: string;
  genderLabel?: string;
}) {
  if (gender === '') {
    return (
      <span
        data-slot="user-card-gender"
        className="size-5.5 shrink-0 rounded-full border border-border"
        aria-hidden
      />
    );
  }

  const kind = resolveGenderIcon(gender);
  const iconId = genderIconId(kind);

  if (kind === 'male' || kind === 'female') {
    return (
      <span
        data-slot="user-card-gender"
        className="flex size-5.5 shrink-0 items-center justify-center rounded-full bg-user-card-gender-accent"
        aria-label={kind === 'male' ? 'Male' : 'Female'}
      >
        <Icon spec={{ type: 'builtin', id: iconId }} size={12} />
      </span>
    );
  }

  const label = genderDisplayLabel(gender, kind, genderLabel);

  return (
    <span
      data-slot="user-card-gender"
      className="flex h-5.5 shrink-0 items-center gap-1 rounded-xl bg-user-card-gender-accent px-1.5 text-xs leading-none text-primary-foreground"
    >
      <Icon spec={{ type: 'builtin', id: iconId }} size={12} />
      <span>{label}</span>
    </span>
  );
}

function UserAvatar({
  name,
  avatarSrc,
  avatarColor,
  avatar,
}: {
  name: string;
  avatarSrc?: string;
  avatarColor?: string;
  avatar?: React.ReactNode;
}) {
  if (avatar) {
    return <div className="relative shrink-0">{avatar}</div>;
  }
  return (
    <div className="relative shrink-0">
      {/* 底色只挂在兜底层上，不挂根：根上那份会留在**已经画出来的**头像底下，而头像本来可以带
          透明像素（挖空的 logo、透明底 PNG），于是照片「已经出来了」却还顶着一块底色。 */}
      <Avatar className="size-22 after:hidden border-2 border-background">
        {avatarSrc ? <AvatarImage src={avatarSrc} alt={name} /> : null}
        {/* 取不到 / 没给图时只留 `avatarColor` 圆底（等图那段由 `AvatarImage` 铺骨架，不提前上
            色），**不画本地首字母**：「没上传头像的人长什么样」由发头像的那一侧定（服务端为每个
            人生成默认头像图），本地再画一份就是第二套实现，还必然对不上——曾经把白色首字母压在
            浅色 `avatarColor` 上，看着就是一枚偏白的空圆。 */}
        <AvatarFallback
          style={avatarColor ? { backgroundColor: avatarColor } : undefined}
        />
      </Avatar>
    </div>
  );
}

function UserCard({
  name,
  username,
  avatarSrc,
  avatarColor,
  avatar,
  gender,
  genderLabel,
  bio,
  bioMoreLabel = 'More',
  bioLessLabel = 'Less',
  fields,
  actions,
  className,
  ...props
}: UserCardProps) {
  return (
    <div
      data-slot="user-card"
      className={cn(
        'relative flex w-full flex-col items-center overflow-hidden rounded-card bg-muted p-1.5 pb-5',
        className,
      )}
      {...props}
    >
      <div
        data-slot="user-card-background"
        className="background-image h-24 w-full shrink-0 rounded-card bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${headBackground})` }}
      />
      <div className="relative z-10 -mt-11 flex w-full flex-col gap-2 px-4">
        <UserAvatar
          name={name}
          avatarSrc={avatarSrc}
          avatarColor={avatarColor}
          avatar={avatar}
        />
        <div data-slot="user-card-identity" className="flex flex-col  gap-1">
          <div className="flex  gap-1.5">
            <p className="text-xl font-semibold leading-tight text-foreground">
              {name}
            </p>
            {gender !== undefined && gender !== '__EMPTY__' ? (
              <Gender gender={gender} genderLabel={genderLabel} />
            ) : null}
          </div>
          {username ? (
            <p className="text-sm leading-none text-muted-foreground">
              {displayUsername(username)}
            </p>
          ) : null}
        </div>
        {bio ? (
          <ExpandableBio
            text={bio}
            moreLabel={bioMoreLabel}
            lessLabel={bioLessLabel}
          />
        ) : null}
        {fields && fields.length > 0 ? (
          <div data-slot="user-card-fields" className="flex w-full flex-col">
            {fields.map((field) => (
              <ProfileFieldRow key={field.id} field={field} />
            ))}
          </div>
        ) : null}

        {actions ? (
          <div
            data-slot="user-card-actions"
            className="mt-1 flex w-full items-center gap-3"
          >
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { UserCard };
